import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface AuditEntry {
  AuditID:      number;
  ActorName:    string;
  ActorEmail:   string;
  Action:       string;
  ProjectTitle: string | null;
  EntityType:   string | null;
  OldValue:     string | null;
  NewValue:     string | null;
  CreatedAt:    string;
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function actionColor(action: string) {
  if (action.includes('Approved'))  return 'badge-success';
  if (action.includes('Denied'))    return 'badge-danger';
  if (action.includes('Submitted')) return 'badge-info';
  if (action.includes('Revision'))  return 'badge-warning';
  return 'badge-neutral';
}

const fieldStyle: React.CSSProperties = {
  padding: '7px 10px', fontSize: 13,
  border: '1px solid var(--border2)', borderRadius: 6,
  background: 'var(--surface)', color: 'var(--text1)', fontFamily: 'var(--font)',
};

// ── Clear Audit Entries Modal ─────────────────────────────────────────────────
const CLEARABLE_USERS = [
  { label: 'Jay Pedersen',  email: 'jpedersen@thewoodlands-tx.gov' },
  { label: 'Brooke Ryan',   email: 'baryan@thewoodlands-tx.gov'    },
];

function ClearAuditButton({ onCleared }: { onCleared: () => void }) {
  const [open,     setOpen]     = useState(false);
  const [working,  setWorking]  = useState(false);
  const [result,   setResult]   = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>(
    CLEARABLE_USERS.map(u => u.email)
  );

  function toggleEmail(email: string, checked: boolean) {
    setSelected(prev => checked ? [...prev, email] : prev.filter(x => x !== email));
  }

  async function doClear() {
    if (selected.length === 0) return;
    setWorking(true);
    setResult(null);
    try {
      const res = await api.admin.audit.clearByUsers(selected);
      setResult(`✓ Deleted ${res.deleted} entries`);
      setTimeout(() => { setOpen(false); setResult(null); onCleared(); }, 1500);
    } catch (e: any) {
      setResult('Error: ' + (e?.message ?? 'unknown'));
      setWorking(false);
    }
  }

  return (
    <>
      <button className="btn btn-sm" style={{ color: 'var(--danger, #dc2626)', borderColor: 'var(--danger, #dc2626)' }} onClick={() => { setOpen(true); setResult(null); }}>
        🗑 Clear Entries
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => !working && setOpen(false)}
        >
          <div
            style={{
              background: 'var(--surface)', borderRadius: 10, padding: 28,
              width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>Clear Audit Log Entries</h3>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 18px', lineHeight: 1.5 }}>
              Permanently deletes <strong>all</strong> audit entries for the selected users.
              This cannot be undone.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {CLEARABLE_USERS.map(u => (
                <label
                  key={u.email}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(u.email)}
                    onChange={e => toggleEmail(u.email, e.target.checked)}
                    disabled={working}
                  />
                  <span style={{ fontWeight: 500 }}>{u.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</span>
                </label>
              ))}
            </div>

            {result && (
              <div style={{
                fontSize: 13, marginBottom: 14,
                color: result.startsWith('✓') ? 'var(--success-text, #065f46)' : 'var(--danger, #dc2626)',
              }}>
                {result}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setOpen(false)} disabled={working}>
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{
                  background: selected.length > 0 && !working ? 'var(--danger, #dc2626)' : undefined,
                  color: selected.length > 0 && !working ? '#fff' : undefined,
                  opacity: selected.length === 0 || working ? 0.5 : 1,
                }}
                onClick={doClear}
                disabled={working || selected.length === 0}
              >
                {working
                  ? 'Deleting...'
                  : `Delete for ${selected.length} user${selected.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function AuditPage() {
  const [entries,  setEntries]  = useState<AuditEntry[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin.audit.list({
      search:   search   || undefined,
      dateFrom: dateFrom || undefined,
      dateTo:   dateTo   || undefined,
    })
      .then(rows => setEntries(rows as AuditEntry[]))
      .catch(err => setError(err?.message ?? 'Failed to load audit log'))
      .finally(() => setLoading(false));
  }, [search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  function exportCSV() {
    const headers = ['Date', 'Actor', 'Email', 'Action', 'Project', 'Entity', 'Old Value', 'New Value'];
    const rows = entries.map(e => [
      formatDateTime(e.CreatedAt), e.ActorName, e.ActorEmail,
      e.Action, e.ProjectTitle || '', e.EntityType || '',
      e.OldValue || '', e.NewValue || '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-sub">Full history of all actions taken in the portal</p>
        </div>
      </div>

      {/* Filters + actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="field-input"
          style={{ width: 260, fontSize: 13 }}
          placeholder="Search actions or people..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>From</span>
          <input type="date" style={fieldStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>To</span>
          <input type="date" style={fieldStyle} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        {(search || dateFrom || dateTo) && (
          <button className="btn btn-sm" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>
            Clear filters
          </button>
        )}
        <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 'auto' }}>
          {loading ? 'Loading...' : `${entries.length} entries`}
        </span>
        <button className="btn btn-sm" onClick={exportCSV} disabled={entries.length === 0}>
          ⬇ Export CSV
        </button>
        <ClearAuditButton onCleared={load} />
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>Loading audit log...</div>
      ) : error ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--danger, #dc2626)' }}>
          {error}&nbsp;—&nbsp;
          <button className="btn btn-sm" onClick={load}>Retry</button>
        </div>
      ) : entries.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>No entries found.</div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Date & Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Project</th>
                <th>Entity</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <React.Fragment key={e.AuditID}>
                  <tr
                    style={{ cursor: (e.OldValue || e.NewValue) ? 'pointer' : 'default' }}
                    onClick={() => (e.OldValue || e.NewValue) && setExpanded(expanded === e.AuditID ? null : e.AuditID)}
                  >
                    <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.CreatedAt)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{e.ActorName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{e.ActorEmail}</div>
                    </td>
                    <td>
                      <span className={`badge ${actionColor(e.Action)}`}>{e.Action}</span>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{e.ProjectTitle || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{e.EntityType || '—'}</td>
                    <td style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>
                      {(e.OldValue || e.NewValue) ? (expanded === e.AuditID ? '▲' : '▼') : ''}
                    </td>
                  </tr>
                  {expanded === e.AuditID && (
                    <tr style={{ background: 'var(--surface2, #f8fafc)' }}>
                      <td colSpan={6} style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {e.OldValue && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Previous Value</div>
                              <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {e.OldValue}
                              </div>
                            </div>
                          )}
                          {e.NewValue && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>New Value</div>
                              <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {e.NewValue}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
